import React, { useState, useEffect } from 'react'
import { ChevronDownIcon, UserIcon } from '../icons/icons'

/** A single navigation element */
export interface NavElement {
  /** Display name for the menu item */
  name: string
  /** Route path (e.g. "/dashboard") */
  route: string
  /** Optional icon rendered before the name */
  icon?: React.ReactNode
}

/** A group of navigation elements with an expandable header */
export interface NavGroup {
  /** Unique group identifier (used for expand/collapse tracking) */
  id: string
  /** Display name for the group header */
  name: string
  /** Optional icon shown in the group header */
  icon?: React.ReactNode
  /** Navigation items inside this group */
  items: NavElement[]
}

export interface AppSidebarProps {
  elements?: NavElement[]
  groups?: NavGroup[]
  /** Currently active route path — used to highlight the active link */
  currentPath: string
  /** Whether the sidebar is in collapsed (icon-only) mode */
  navCollapsed?: boolean
  /** Record of which group IDs are expanded, keyed by group id */
  expandedGroups?: Record<string, boolean>
  /** Called when a group header is clicked to toggle expand/collapse */
  onToggleGroup?: (groupId: string) => void
  /** Username displayed in the bottom user section */
  userName?: string
  /** Called when the disconnect button is clicked */
  onDisconnect?: () => void
  /** SPA navigation function */
  navigate?: (route: string) => void
  /** Called when mobile backdrop is clicked (user wants to close drawer) */
  onMobileBackdropClick?: () => void
  /** Theme — defaults to 'dark' */
  theme?: 'dark' | 'light'
}

// ─── Theme tokens ─────────────────────────────────────────────────────────────
// Uses CSS variables from design system (colors-dark.css and colors-light.css)

const THEMES = {
  dark: {
    bg: 'var(--color-bg-secondary)',
    bgHover: 'rgba(255,255,255,0.06)',
    bgActive: 'var(--color-primary-glow)',
    border: 'var(--color-border)',
    borderActive: 'var(--color-primary)',
    text: 'var(--color-text-secondary)',
    textActive: 'var(--color-primary)',
    textHover: '#f8fafc',
    accent: 'var(--color-primary)',
  },
  light: {
    bg: 'var(--color-bg-secondary)',
    bgHover: 'var(--color-bg-tertiary)',
    bgActive: 'var(--color-primary-glow)',
    border: 'var(--color-border)',
    borderActive: 'var(--color-primary)',
    text: 'var(--color-text-secondary)',
    textActive: 'var(--color-primary)',
    textHover: 'var(--color-text-primary)',
    accent: 'var(--color-primary)',
  },
} as const

// ─── Component ────────────────────────────────────────────────────────────────

export function AppSidebar({
  elements,
  groups,
  currentPath,
  navCollapsed = true,
  expandedGroups = {},
  onToggleGroup,
  userName,
  onDisconnect,
  navigate,
  onMobileBackdropClick,
  theme = 'dark',
}: AppSidebarProps): React.ReactElement {
  const t = THEMES[theme]
  const [hoveredRoute, setHoveredRoute] = useState<string | null>(null)
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768)

  // Track viewport changes to sync isMobile state
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Sidebar width: 80px when collapsed (icon-only), 210px when expanded (full labels)
  const collapsedWidth = 80

  const navStyle: React.CSSProperties = {
    position: 'fixed',
    left: 0,
    top: isMobile ? 0 : 70,
    width: navCollapsed ? collapsedWidth : 210,
    height: isMobile ? '100vh' : 'calc(100vh - 70px)',
    background: t.bg,
    borderRight: `1px solid ${t.border}`,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    overflowX: 'hidden',
    zIndex: !navCollapsed ? 920 : 100, // var(--z-index-drawer-content)
    transition: 'left 0.3s ease, width 0.3s ease',
    boxShadow: isMobile && !navCollapsed ? 'var(--shadow-lg)' : 'none',
  }

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, route: string) => {
    if (navigate) {
      e.preventDefault()
      navigate(route)
    }
  }

  const linkStyle = (route: string): React.CSSProperties => {
    const isActive = currentPath === route
    const isHovered = hoveredRoute === route
    return {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: navCollapsed ? '14px 0' : '12px 20px',
      margin: navCollapsed ? '2px 8px' : '0',
      borderRadius: navCollapsed ? 8 : 0,
      justifyContent: navCollapsed ? 'center' : 'flex-start',
      color: isActive ? t.textActive : isHovered ? t.textHover : t.text,
      background: isActive ? t.bgActive : isHovered ? t.bgHover : 'transparent',
      textDecoration: 'none',
      fontSize: 14,
      fontWeight: isActive ? 600 : 500,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      whiteSpace: 'nowrap',
    }
  }

  const renderNavLink = (item: NavElement) => (
    <a
      key={item.route}
      href={item.route}
      title={navCollapsed ? item.name : undefined}
      style={linkStyle(item.route)}
      onClick={(e) => handleLinkClick(e, item.route)}
      onMouseEnter={() => setHoveredRoute(item.route)}
      onMouseLeave={() => setHoveredRoute(null)}
      aria-current={currentPath === item.route ? 'page' : undefined}
      aria-label={navCollapsed ? item.name : undefined}
    >
      <span aria-hidden="true">{item.icon}</span>
      {!navCollapsed && <span>{item.name}</span>}
    </a>
  )

  const renderUserSection = () => {
    if (!userName && !onDisconnect) return null
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: navCollapsed ? '12px 8px' : '12px 20px',
        borderTop: `1px solid ${t.border}`,
        marginTop: 'auto',
      }}>
        {userName && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: navCollapsed ? 'center' : 'flex-start',
            gap: 8,
            color: t.accent,
            fontSize: 12,
            fontWeight: 600,
          }}>
            <UserIcon />
            {!navCollapsed && <span>{userName.length > 12 ? `${userName.substring(0, 12)}...` : userName}</span>}
          </div>
        )}
        {onDisconnect && !navCollapsed && (
          <button
            onClick={onDisconnect}
            style={{
              background: 'none',
              border: '1px solid red',
              padding: '3px 6px',
              color: 'red',
              fontSize: 10,
              fontWeight: 500,
              cursor: 'pointer',
              textAlign: 'center',
              borderRadius: 4,
              width: '140px',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'red'
              e.currentTarget.style.color = 'white'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent'
              e.currentTarget.style.color = 'red'
            }}
          >
            Disconnect
          </button>
        )}
      </div>
    )
  }

  const linksStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    padding: '12px 0',
    overflowY: 'auto',
  }

  // Mobile menu backdrop (closes menu when clicked) — only on mobile viewport and when expanded
  // Starts at 70px (below header) so header remains fully interactive
  const mobileBackdrop = isMobile && !navCollapsed ? (
    <div
      onClick={() => onMobileBackdropClick?.()}
      role="presentation"
      style={{
        position: 'fixed',
        top: 70,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        zIndex: 910, // var(--z-index-drawer-backdrop)
        cursor: 'pointer',
      }}
      aria-hidden="true"
    />
  ) : null

  // `elements` (flat, no header) and `groups` (collapsible sections) are independent and can be
  // combined: ungrouped items render first, exactly as flat-only mode always has, followed by any
  // groups below them. A caller passing only one of the two sees identical output to before this
  // combined render replaced the old either/or branches — this is additive, not a behavior change
  // for existing single-mode callers.
  const navClassName = 'app-nav'
  return (
    <>
      {mobileBackdrop}
      <nav className={navClassName} style={navStyle}>
        <div style={linksStyle}>
        {(elements || []).map(renderNavLink)}
        {(groups || []).map(group => (
          <div key={group.id} style={{ borderBottom: `1px solid ${t.border}` }}>
            <button
              onClick={() => onToggleGroup?.(group.id)}
              aria-expanded={expandedGroups[group.id]}
              title={navCollapsed ? group.name : undefined}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: navCollapsed ? '14px 0' : '12px 20px',
                width: '100%',
                // Deliberately NOT t.textActive/t.bgActive here — those are reserved for "this is
                // the page you're on" (see linkStyle above). A group being expanded is a distinct
                // state ("this section is open") and must stay visually neutral, or it becomes
                // indistinguishable from an actually-selected link inside it.
                color: expandedGroups[group.id] ? t.textHover : t.text,
                background: expandedGroups[group.id] ? t.bgHover : 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                textAlign: 'left',
                justifyContent: navCollapsed ? 'center' : 'flex-start',
                transition: 'all 0.2s ease',
              }}
            >
              {navCollapsed && <span style={{ opacity: expandedGroups[group.id] ? 1 : 0.4, transition: 'opacity 0.2s ease' }}>{group.icon}</span>}
              {!navCollapsed && <span style={{ flex: 1 }}>{group.name}</span>}
              {!navCollapsed && (
                <span style={{ transform: expandedGroups[group.id] ? 'rotate(-180deg)' : 'none', transition: 'transform 0.3s' }}>
                  <ChevronDownIcon />
                </span>
              )}
            </button>
            {expandedGroups[group.id] && (
              <div style={{ padding: navCollapsed ? '0 8px' : '0' }}>
                {group.items.map(renderNavLink)}
              </div>
            )}
          </div>
        ))}
        </div>
        {renderUserSection()}
      </nav>
    </>
  )
}

export default AppSidebar
